import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  addExadisReturnListItemRequestSaga,
  getExadisReturnListAddItemStatus,
  setExadisReturnListAddItemStatus,
} from 'domains/order/Order.store';
import { AddExadisReturnListItemSelection } from 'domains/order/Order.types';
import { BlackButton, Flex, InputNumber, Text, WhiteButton } from 'UI';
import { isLoading } from 'utils';

interface ExadisReturnQuantityPopoverProps {
  item: AddExadisReturnListItemSelection;
  onClose: () => void;
}

const MIN_QUANTITY = 1;
const DEFAULT_MAX_QUANTITY = 99;

const parseSourceInvoicedQuantity = (sourceInvoicedQuantity: number | undefined): number => {
  if (sourceInvoicedQuantity === undefined || sourceInvoicedQuantity === null) {
    return DEFAULT_MAX_QUANTITY;
  }
  return sourceInvoicedQuantity < MIN_QUANTITY ? DEFAULT_MAX_QUANTITY : sourceInvoicedQuantity;
};

const ExadisReturnQuantityPopover = ({ item, onClose }: ExadisReturnQuantityPopoverProps): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const addItemStatus = useSelector(getExadisReturnListAddItemStatus);
  const maxQuantity = parseSourceInvoicedQuantity(item.sourceInvoicedQuantity);
  const [quantity, setQuantity] = useState<number | null>(1);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    setQuantity(1);
    setHasError(false);
    dispatch(setExadisReturnListAddItemStatus(undefined));
  }, [item, dispatch]);

  const validate = (value: number | null): boolean =>
    value === null || Number.isNaN(value) || value < MIN_QUANTITY || value > maxQuantity;

  const handleQuantityChange = (value?: string) => {
    const numericValue = value === undefined || value === '' ? null : Number(value);
    setQuantity(numericValue);
    setHasError(validate(numericValue));
  };

  const handleQuantityBlur = (value?: number) => {
    const numericValue = value === undefined || Number.isNaN(value) ? null : value;
    setQuantity(numericValue);
    setHasError(validate(numericValue));
  };

  const handleAdd = () => {
    const isInvalid = validate(quantity);
    if (isInvalid || quantity === null) {
      setHasError(isInvalid);
      return;
    }

    dispatch(
      addExadisReturnListItemRequestSaga({
        salesOrderNumber: item.salesOrderNumber,
        invoiceId: item.invoiceId,
        invoiceLineNumber: item.invoiceLineNumber,
        reference: item.reference,
        requestedQuantity: quantity,
      }),
    );
    onClose();
  };

  return (
    <Flex direction={'column'} gap={10}>
      <Text type={'h4_paragraph'}>{t('exadis_return_list.modal.title', 'Quantity to return')}</Text>
      <InputNumber
        min={MIN_QUANTITY}
        max={maxQuantity}
        value={quantity ?? undefined}
        onChange={handleQuantityChange}
        onBlur={handleQuantityBlur}
        status={hasError ? 'error' : undefined}
        data-cy={'exadis-return-quantity-popover-input'}
      />
      <Flex justify={'flex-end'} gap={10}>
        <WhiteButton onClick={onClose} data-cy={'exadis-return-quantity-popover-cancel'}>
          {t('common.action.cancel', 'Cancel')}
        </WhiteButton>
        <BlackButton
          onClick={handleAdd}
          disabled={!!hasError || quantity === null || isLoading(addItemStatus)}
          data-cy={'exadis-return-quantity-popover-add'}
        >
          {t('exadis_return_list.modal.add', 'Add to return list')}
        </BlackButton>
      </Flex>
    </Flex>
  );
};

export default ExadisReturnQuantityPopover;
